import { useEffect, useState } from 'react';
import { initPostHog } from '../posthog.js';

// Cookie-consent gate for PostHog product analytics. Applies uniformly to every
// visitor (no geo/EU logic). PostHog is NOT initialised on load — it fires only
// once, and only after this gate reads a stored decision or the visitor accepts:
//   'granted' → init PostHog silently, no banner
//   'denied'  → skip PostHog entirely, no banner
//   unset     → show the banner; PostHog stays off until a choice is made
const CONSENT_KEY = 'cookie_consent';

function readConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY);
  } catch {
    // Private mode / blocked storage — treat as undecided (show the banner).
    return null;
  }
}

// True once the visitor has explicitly Accepted or Rejected cookies. The install
// banner uses this to hold off until the cookie banner has been dealt with, so
// the two never sit on top of each other at the bottom of the screen.
export function hasCookieDecision() {
  const c = readConsent();
  return c === 'granted' || c === 'denied';
}

// Fired the moment the visitor taps Accept or Reject. The install banner listens
// for this and reveals itself a few seconds later, on the same visit, without
// ever overlapping the cookie banner (which is gone by then).
export const COOKIE_DECISION_EVENT = 'bs-cookie-decided';

function writeConsent(value) {
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    // Best-effort: if storage is blocked the choice can't persist, but we still
    // honour it for this page load below.
  }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = readConsent();
    if (consent === 'granted') {
      initPostHog(); // returning, already-consented visitor — init silently
    } else if (consent === 'denied') {
      // Explicitly rejected — never init PostHog, never show the banner.
    } else {
      setVisible(true); // no decision yet — ask
    }
  }, []);

  // While the banner is up, flag the body so the login/signup screens can add
  // scroll room and keep their buttons clear of this fixed bar (see styles.css).
  useEffect(() => {
    document.body.classList.toggle('bs-cookie-open', visible);
    return () => document.body.classList.remove('bs-cookie-open');
  }, [visible]);

  if (!visible) return null;

  function accept() {
    writeConsent('granted');
    initPostHog();
    setVisible(false);
    window.dispatchEvent(new Event(COOKIE_DECISION_EVENT));
  }

  function reject() {
    writeConsent('denied');
    setVisible(false);
    window.dispatchEvent(new Event(COOKIE_DECISION_EVENT));
  }

  return (
    <div className="cookie-consent" role="dialog" aria-label="Cookie consent" aria-live="polite">
      {/* The Privacy Policy link was removed from the banner per the Sprint 1
          brief. The policy stays one tap away elsewhere (Login, Sign Up, and
          Settings → Data & Privacy all link to /privacy), so consent is still
          informed under NDPA 2023. */}
      <p className="cookie-consent-text">
        We use cookies to understand how Bragsheet is used and improve it.
      </p>
      <div className="cookie-consent-actions">
        <button type="button" className="cookie-consent-btn" onClick={reject}>
          Reject
        </button>
        <button type="button" className="cookie-consent-btn" onClick={accept}>
          Accept
        </button>
      </div>
    </div>
  );
}
